import { useEffect, useState } from "react"
import { Modal } from "common/Modal"
import UserService from "services/user.service"
import { useNotifications } from "hooks/useNotification"

const service = new UserService()
const INPUT_CLASS = "w-full px-4 py-2 font-light text-gray-800 border border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"

type Props = {
    showModal: boolean
    setShowModal: (value: boolean) => void
    onSuccess: () => void
}

const initialForm = {
    badgeId: "",
    startDate: "",
    endDate: "",
    cityName: "",
    state: "",
    description: "",
}

export const AddTripModal = ({ showModal, setShowModal, onSuccess }: Props) => {
    const [form, setForm] = useState(initialForm)
    const [saving, setSaving] = useState(false)
    const { successMessage, errorMessage } = useNotifications()

    useEffect(() => {
        if (showModal) setForm(initialForm)
    }, [showModal])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.badgeId?.trim()) {
            errorMessage("Please enter Badge ID")
            return
        }
        if (!form.startDate) {
            errorMessage("Please select Start Date")
            return
        }
        if (!form.endDate) {
            errorMessage("Please select End Date")
            return
        }
        if (!form.cityName?.trim()) {
            errorMessage("Please enter City")
            return
        }
        if (!form.state?.trim()) {
            errorMessage("Please enter State")
            return
        }
        
        setSaving(true)
        try {
            await service.addTrip({
                badgeId: form.badgeId.trim(),
                startDate: form.startDate,
                endDate: form.endDate,
                cityName: form.cityName.trim(),
                state: form.state.trim(),
                description: form.description?.trim() || undefined,
            })
            successMessage("Trip added successfully")
            setForm(initialForm)
            setShowModal(false)
            onSuccess()
        } catch (error: any) {
            errorMessage(error?.response?.data?.message || "Failed to add trip")
            console.log(error)
        } finally {
            setSaving(false)
        }
    }

    const handleCancel = () => {
        setShowModal(false)
        setForm(initialForm)
    }

    return (
        <Modal
            showModal={showModal}
            setShowModal={setShowModal}
            title="Add Trip"
            width="w-full md:w-1/2"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col gap-1.5">
                    <label className="field-label">
                        Badge ID <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.badgeId}
                        onChange={(e) => setForm((prev) => ({ ...prev, badgeId: e.target.value }))}
                        required
                        className={INPUT_CLASS}
                        placeholder="e.g. 2719"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                        <label className="field-label">
                            Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.startDate}
                            onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
                            required
                            className={INPUT_CLASS}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="field-label">
                            End Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.endDate}
                            onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
                            required
                            className={INPUT_CLASS}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                        <label className="field-label">
                            City <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.cityName}
                            onChange={(e) => setForm((prev) => ({ ...prev, cityName: e.target.value }))}
                            required
                            className={INPUT_CLASS}
                            placeholder="e.g. Lahore"
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="field-label">
                            State <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.state}
                            onChange={(e) => setForm((prev) => ({ ...prev, state: e.target.value }))}
                            required
                            className={INPUT_CLASS}
                            placeholder="e.g. LA"
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-1.5">
                    <label className="field-label">Description</label>
                    <textarea
                        value={form.description}
                        onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className={INPUT_CLASS}
                        placeholder="e.g. dinner with team"
                    />
                </div>

                <div className="flex gap-3 pt-4">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex-1 px-4 py-2 text-white bg-green-500 rounded-md hover:bg-green-600 disabled:bg-green-300 disabled:cursor-not-allowed transition-colors"
                    >
                        {saving ? "Adding..." : "Add Trip"}
                    </button>
                </div>
            </form>
        </Modal>
    )
}
